/**
 * Billing-specific formatting helpers.
 *
 * Kept separate from lib/dates.js because these are billing-specific
 * concerns (invoice status semantics, subscription lifecycle states,
 * money formatting in multiple contexts). lib/dates.js stays general.
 */

/**
 * Format pence as pounds — British, precise.
 * 500 → "£5.00", 1250 → "£12.50", 0 → "£0.00"
 */
export function formatMoney(pence) {
  if (pence === null || pence === undefined || Number.isNaN(pence)) return '—';
  const pounds = (pence / 100).toFixed(2);
  return `£${pounds}`;
}

/**
 * Subscription status labels — mapped from Stripe's canonical strings
 * to UK-friendly admin-facing copy.
 */
export function subscriptionStatusLabel(status) {
  switch (status) {
    case 'trialing':  return 'Trial';
    case 'active':    return 'Active';
    case 'past_due':  return 'Payment failed';
    case 'suspended': return 'Suspended';
    case 'cancelled': return 'Cancelled';
    case 'canceled':  return 'Cancelled';  // Stripe's American spelling
    case 'unpaid':    return 'Unpaid';
    case 'incomplete':
    case 'incomplete_expired':
      return 'Setup incomplete';
    case 'founder':   return 'Founder';
    default:          return status || '—';
  }
}

/**
 * CSS class name for the status pill.
 */
export function subscriptionStatusClass(status) {
  switch (status) {
    case 'active':
    case 'founder':
      return 'sub-status-active';
    case 'trialing':
      return 'sub-status-trial';
    case 'past_due':
    case 'unpaid':
      return 'sub-status-warn';
    case 'suspended':
      return 'sub-status-danger';
    case 'cancelled':
    case 'canceled':
    case 'incomplete':
    case 'incomplete_expired':
      return 'sub-status-muted';
    default:
      return 'sub-status-muted';
  }
}

/**
 * Invoice status — Stripe statuses are canonical.
 * Possible values: draft, open, paid, void, uncollectible
 */
export function invoiceStatusLabel(status) {
  switch (status) {
    case 'draft':         return 'Draft';
    case 'open':          return 'Unpaid';
    case 'paid':          return 'Paid';
    case 'void':          return 'Void';
    case 'uncollectible': return 'Write-off';
    default:              return status || '—';
  }
}

export function invoiceStatusClass(status) {
  switch (status) {
    case 'paid':          return 'inv-status-paid';
    case 'open':          return 'inv-status-open';
    case 'draft':         return 'inv-status-draft';
    case 'void':
    case 'uncollectible':
      return 'inv-status-void';
    default:              return 'inv-status-draft';
  }
}

/**
 * Format a trial remaining countdown.
 * daysLeft >= 1 → "X days left" / "1 day left"
 * daysLeft < 1  → "Trial ending today" / "Trial ended"
 */
export function formatTrialRemaining(trial) {
  if (!trial) return null;
  if (trial.expired) return 'Trial ended';
  if (trial.daysLeft === 0) return 'Trial ending today';
  if (trial.daysLeft === 1) return '1 day left in trial';
  return `${trial.daysLeft} days left in trial`;
}

/**
 * Card description — "Visa ending 4242" / "Mastercard ending 0000"
 * from a PaymentMethod object: { brand, last4 }
 */
export function formatCard(pm) {
  if (!pm) return 'No card on file';
  const brand = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  }[pm.brand?.toLowerCase()] || (pm.brand || 'Card');
  return `${brand} ending ${pm.last4}`;
}

/**
 * Is the card expiring soon? Used to show an amber warning.
 * Input: { exp_month (1-12), exp_year (YYYY) }
 * Returns true if expiring this month or next.
 */
export function isCardExpiringSoon(pm) {
  if (!pm?.exp_month || !pm?.exp_year) return false;
  const now = new Date();
  const thisMonth = now.getMonth() + 1;   // 1-12
  const thisYear = now.getFullYear();
  const monthsUntilExpiry =
    (pm.exp_year - thisYear) * 12 + (pm.exp_month - thisMonth);
  return monthsUntilExpiry <= 1 && monthsUntilExpiry >= 0;
}
